import React, { useEffect, useState } from 'react'

import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@interco/inter-ui/components/Alert'
import { Page } from '@templates'
import ChevronRight from '@interco/icons/core/action-navigation/ic_chevron_right'
import { ApplicationState } from '@store/types'
import Attention from '@interco/icons/core/status/ic_warning_circle'
import Refresh from '@interco/icons/core/action-navigation/ic_rotate_right'
import SolidClose from '@interco/icons/core/status/ic_close_circle_fill'
import CheckFill from '@interco/icons/core/status/ic_check_circle_fill'
import { BridgeService } from '@services/bridge'
import { ColorWrapper, TextParagraph, Box } from '@atoms'
import { Colors } from '@utils/Colors'
import { StepRefin } from '@utils/enums'
import { Button } from '@interco/inter-ui/components/Button'
import routes from '@routes/routes'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { isSerasaWithDocuments } from '@utils/functions'
import { useTrackingRefin } from '@hooks'

import { WrapText } from '../styles'
import BottomsheetTipoDoc from './BottomsheetTipoDoc'
import { sendAppDocumentTag, sendWebDocumentTag } from './tracking'
import { useStatusDocumentosHelpers } from './useStatusDocumentosHelpers'

const StatusDocumentos = () => {
  useTrackingRefin(StepRefin.STATUS_DOCUMENTOS)

  const {
    executeStatusDocumentosContinue,
    getIdentificacaoDocumentCaption,
    getIsButtonDisabled,
    getStatusDocumentosHeadingTitle,
    getStatusDocumentosViewFlags,
  } = useStatusDocumentosHelpers()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [openTipoDoc, setOpenTipoDoc] = useState(false)
  const { isError, isDone } = useSelector(
    (state: ApplicationState) => state.refinanciamento.fotoDocumentos,
  )
  const { isError: isErrorContraCheque, isDone: isDoneContraCheque } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contrachequeDocumentos,
  )
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)
  const { biometricActive, loadingGenerateIdUnicoLink } = useSelector(
    (state: ApplicationState) => state.biometria,
  )

  const {
    showsContracheque,
    hasOtherInAppDocuments,
    idCloudWithDocumentsActive,
    isContinuarIdUnicoLoading,
    showStatusDocumentosErrorAlert,
  } = getStatusDocumentosViewFlags({
    convenioSelecionado: convenioCodigo,
    isError,
    biometricActive,
    loadingGenerateIdUnicoLink,
  })

  useEffect(() => {
    BridgeService.tac({
      name: 'envio_documentos',
      actions: ['buscar_documentos', 'solicitar_biometria'],
    })

    dispatch(
      trackingRefin({
        step: StepRefin.STATUS_DOCUMENTOS,
        screen_name: 'clt:refin:documents',
        section: 'documents',
        scenario: 'success',
      }),
    )

    if (isApp) {
      sendAppDocumentTag()
    } else {
      sendWebDocumentTag()
    }
  }, [dispatch, isApp])

  useEffect(() => {
    if (!isError) return

    dispatch(
      trackingRefin({
        step: StepRefin.ERRO_STATUS_DOCUMENTOS,
        screen_name: 'clt:refin:documents',
        section: 'documents',
        scenario: 'error',
      }),
    )

    if (isApp) {
      sendAppDocumentTag({ scenario: 'error', sub_section: 'error' })
    } else {
      sendWebDocumentTag({ sub_section: 'error', scenario: 'error' })
    }
  }, [dispatch, isError, isApp])

  return (
    <Page
      titleHeader="Contratação"
      stickyFooter={
        <>
          {showStatusDocumentosErrorAlert && (
            <Alert customBackgroundColor={Colors.ERROR500} customIcon={<Attention color="white" />}>
              <ColorWrapper color={Colors.WHITE} fontWeight="700">
                <TextParagraph variant="body-3">Ocorreu um erro</TextParagraph>
              </ColorWrapper>
            </Alert>
          )}
          <Button
            disabled={
              isContinuarIdUnicoLoading ||
              getIsButtonDisabled({
                convenioSelecionado: convenioCodigo,
                hasOtherInAppDocuments,
                idCloudWithDocumentsActive,
                isDone,
                isDoneContraCheque,
                showsContracheque,
              })
            }
            isLoading={isContinuarIdUnicoLoading}
            onClick={() => {
              dispatch(
                trackingRefin({
                  step: StepRefin.CLICK_STATUS_DOCUMENTOS_CONTINUAR,
                  screen_name: 'clt:refin:documents',
                  section: 'documents',
                  scenario: 'click',
                }),
              )
              executeStatusDocumentosContinue({
                biometricActive,
                dispatch,
                isApp,
                navigate,
                trackingRefin,
              })
            }}
          >
            Continuar
          </Button>
        </>
      }
    >
      <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0 0 8px 0">
        <TextParagraph variant="headline-h2">
          {getStatusDocumentosHeadingTitle(convenioCodigo)}
        </TextParagraph>
      </ColorWrapper>
      <TextParagraph variant="body-3">
        Você pode tirar uma foto ou enviar uma que já possui em seu celular.
      </TextParagraph>

      {!idCloudWithDocumentsActive ? (
        <Box
          key="foto"
          onClick={() => {
            dispatch(
              trackingRefin({
                step: StepRefin.CLICK_STATUS_DOCUMENTOS_DOCUMENTO_IDENTIFICACAO,
                screen_name: 'clt:refin:documents',
                section: 'documents',
                scenario: 'click',
              }),
            )
            if (isDone) return

            BridgeService.tic({
              name: 'btn_documento_identificacao',
              action: 'selecionar_documento_identificacao',
            })

            if (isApp) {
              sendAppDocumentTag({ ref_type: 'botao', content_action: 'Toque' })
            } else {
              sendWebDocumentTag({ element_action: 'click button' })
            }

            if (isSerasaWithDocuments(biometricActive)) {
              dispatch(trackingRefin(StepRefin.INICIO_CAPTURA_DOCUMENTO_SERASA))
              navigate(routes.DOCUMENTO_IDENTIFICACAO)
              return
            }

            setOpenTipoDoc(true)
          }}
          actived={false}
          style={{
            marginTop: '40px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          {isError ? (
            <>
              <SolidClose height={24} width={24} color={Colors.ERROR500} />
              <WrapText style={{ width: '75%' }}>
                <ColorWrapper color={Colors.GRAY500} fontWeight="700">
                  <TextParagraph variant="body-3">Documento de identificação</TextParagraph>
                </ColorWrapper>
                <ColorWrapper color={Colors.PRIMARY500} fontWeight="700">
                  <TextParagraph variant="caption-1">Conferir dicas pra foto</TextParagraph>
                </ColorWrapper>
              </WrapText>
              <Refresh height={24} width={24} color={Colors.PRIMARY500} />
            </>
          ) : (
            <>
              {isDone ? <CheckFill height={24} width={24} color={Colors.SUCCESS500} /> : <></>}
              <WrapText style={{ width: '75%' }}>
                <ColorWrapper color={Colors.GRAY500} fontWeight="700">
                  <TextParagraph variant="body-3">Documento de identificação</TextParagraph>
                </ColorWrapper>
                <TextParagraph variant="caption-1">
                  {getIdentificacaoDocumentCaption(convenioCodigo)}
                </TextParagraph>
              </WrapText>
              <ChevronRight height={24} width={24} color={Colors.PRIMARY500} />
            </>
          )}
        </Box>
      ) : (
        <></>
      )}

      {showsContracheque ? (
        <Box
          key="contracheque"
          actived={false}
          style={{
            marginTop: idCloudWithDocumentsActive ? '40px' : '16px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
          onClick={() => {
            dispatch(
              trackingRefin({
                step: StepRefin.CLICK_STATUS_DOCUMENTOS_CONTRACHEQUE,
                screen_name: 'clt:refin:documents',
                section: 'documents',
                scenario: 'click',
              }),
            )
            if (isDoneContraCheque) return

            BridgeService.tic({
              name: 'btn_contracheque',
              action: 'selecionar_contracheque',
            })
            if (isApp) {
              sendAppDocumentTag({
                ref_type: 'botao',
                content_action: 'Toque',
              })
            } else {
              sendWebDocumentTag({ element_action: 'click button' })
            }
            navigate(routes.MODO_ENVIO_CONTRA_CHEQUE)
          }}
        >
          {isErrorContraCheque ? (
            <>
              <SolidClose height={24} width={24} color={Colors.ERROR500} />
              <WrapText style={{ width: '75%' }}>
                <ColorWrapper color={Colors.GRAY500} fontWeight="700">
                  <TextParagraph variant="body-3">Contracheque</TextParagraph>
                </ColorWrapper>
                <ColorWrapper color={Colors.PRIMARY500} fontWeight="700">
                  <TextParagraph variant="caption-1">Conferir dicas pra foto</TextParagraph>
                </ColorWrapper>
              </WrapText>
              <Refresh height={24} width={24} color={Colors.PRIMARY500} />
            </>
          ) : (
            <>
              {isDoneContraCheque ? (
                <CheckFill height={24} width={24} color={Colors.SUCCESS500} />
              ) : (
                <></>
              )}
              <WrapText style={{ width: '75%' }}>
                <ColorWrapper color={Colors.GRAY500} fontWeight="700">
                  <TextParagraph variant="body-3">Contracheque</TextParagraph>
                </ColorWrapper>
              </WrapText>
              <ChevronRight height={24} width={24} color={Colors.PRIMARY500} />
            </>
          )}
        </Box>
      ) : (
        <></>
      )}
      {!idCloudWithDocumentsActive && openTipoDoc ? (
        <BottomsheetTipoDoc setOpenTipoDoc={setOpenTipoDoc} />
      ) : (
        <></>
      )}
    </Page>
  )
}

export default StatusDocumentos
